'use client'

import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'

import { PAGES } from '@/constant/siteConstants'
import type { PhotoPayload } from '@/constant/types'
import { cn, formatDate, getSanityImageDims, getSizes } from '@/lib/utils'
import { urlForImage } from '@/sanity/lib/image'
import { useIsDevice } from '@/state/useGlobalState'

type AlbumPreviewProps = {
  as?: React.ElementType
  title: string
  slug: string
  date: string
  photos: PhotoPayload[]
  enableHover: boolean
  className?: string
}

export default function AlbumPreviewThumbs({
  as: El = 'div',
  title,
  slug,
  date,
  photos,
  enableHover,
  className,
}: AlbumPreviewProps) {
  const [hovered, setHovered] = React.useState(false)
  const imageContainerRef = React.useRef<HTMLDivElement>(null)
  const [maxTranslation, setMaxTranslation] = React.useState(0)
  const isDevice = useIsDevice()

  // Calculate the maximum translation distance after component is mounted
  React.useEffect(() => {
    if (!imageContainerRef.current) return

    const calculateMaxTranslation = () => {
      const container = imageContainerRef.current
      if (!container) return

      const containerWidth = container.offsetWidth
      const finalEl = container.querySelector('.final--slide') as HTMLElement
      const { width: finalElWidth, left: finalElLeft } =
        finalEl.getBoundingClientRect()
      const finalWidth = finalElLeft - containerWidth + finalElWidth / 2

      setMaxTranslation(finalWidth)
    }

    calculateMaxTranslation()
    // Recalculate on window resize for responsive behavior
    window.addEventListener('resize', calculateMaxTranslation)
    return () => window.removeEventListener('resize', calculateMaxTranslation)
  }, [photos])

  // Animation effect
  React.useEffect(() => {
    const container = imageContainerRef.current
    if (isDevice || !container || maxTranslation <= 0) return

    const animatePosition = () => {
      // if (!enableHover) return
      const scrollAlong = hovered && enableHover
      const targetX = scrollAlong ? -maxTranslation : 0
      // Calculate duration based on distance to maintain consistent speed
      // Using 20px/second as the base speed
      const distanceToTravel = Math.abs(maxTranslation)
      const baseSpeed = 100 // pixels per second
      const duration = distanceToTravel / baseSpeed

      // Apply the calculated duration, with a minimum of 0.5s and maximum of 30s
      const animationDuration = duration

      container.style.transition = `transform ${scrollAlong ? animationDuration : 1}s ${scrollAlong ? 'linear' : 'ease-in-out'}`
      container.style.transform = `translateX(${targetX}px)`
    }

    animatePosition()
  }, [hovered, maxTranslation, enableHover, isDevice])

  return (
    <El className={cn('', className)}>
      <Link
        className='album--overview-link block w-full'
        href={`/${PAGES.albums.slug}/${slug}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          className={cn('vM:pl-8 relative mb-3 w-full pl-4')}
          style={
            isDevice
              ? {
                  overflowY: 'scroll',
                }
              : {
                  overflow: 'hidden',
                }
          }
        >
          {/* OTHER IMAGES */}
          <div
            ref={imageContainerRef}
            className={cn('flex will-change-transform')}
          >
            {photos.map((photo, index) => {
              const { _key } = photo
              const { width, height } = getSanityImageDims(photo)
              const ratio = width / height
              const thumbProps = {
                default: {
                  height: 160,
                  width: Math.ceil(160 * ratio),
                },
                md: {
                  height: 200,
                  width: Math.ceil(200 * ratio),
                },
              }
              const thumbSizes = {
                md: `${thumbProps.md.width}px`,
              }
              const sizes = getSizes(
                thumbSizes,
                `${thumbProps.default.width}px`,
              )
              // Preload first three
              const priority = index < 3 || hovered
              return (
                <figure
                  key={_key}
                  className='relative mr-6 flex-shrink-0 last:mb-0'
                >
                  <Image
                    src={urlForImage(photo).url()}
                    width={width}
                    height={height}
                    alt={title}
                    placeholder='blur'
                    blurDataURL={photo.asset.metadata.lqip}
                    className={`h-[160px] w-auto rounded-sm md:h-[200px]`}
                    sizes={sizes}
                    priority={priority}
                  />
                </figure>
              )
            })}
            <div className='final--slide flex flex-shrink-0 items-center justify-center'>
              <span className='pr-[60px] pl-6 text-sm font-medium whitespace-nowrap md:pr-[320px]'>
                More Photos →
              </span>
            </div>
          </div>
        </div>
        <p className='title vM:ml-8 ml-4 text-sm'>
          {title}, {formatDate(date)}
        </p>
      </Link>
    </El>
  )
}
